import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveBar } from '@nivo/bar';
import { Tooltip as InfoHint } from '@material-ui/core';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import theme from '../../theme';

import { getMonthlySalesReport } from '../../api/dashboard';
import { MONTH_FULL_LIST } from '../../constants';
import { PROPOSAL_GRAPH_DESCRIPTION } from '../../constants/graphDescriptions';

const PROPOSAL_KEYS = ['Proposals Created', 'Proposals Approved'];

const chartTheme = {
  grid: {
    line: {
      stroke: '#E2E8F0',
      strokeDasharray: '2 4',
    },
  },
  axis: {
    ticks: {
      text: {
        fill: '#64748B',
        fontSize: 11,
      },
    },
  },
  legends: {
    text: {
      fill: '#334155',
      fontSize: 12,
    },
  },
  tooltip: {
    container: {
      background: 'transparent',
      boxShadow: 'none',
      padding: 0,
      border: 'none',
    },
  },
};

const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface ProposalRow {
  month: number;
  year: number;
  proposalsCreated: number;
  proposalsApproved: number;
  createdCount: number;
  approvedCount: number;
}

const MonthlySales = () => {
  const [rows, setRows] = React.useState<ProposalRow[]>([]);

  React.useEffect(() => {
    getMonthlySalesReport()
      .then(({ data }) => {
        setRows(
          (data || []).map((d: any) => ({
            month: Number(d.month),
            year: Number(d.year),
            proposalsCreated: Number(d.proposalsCreated) || 0,
            proposalsApproved: Number(d.proposalsApproved) || 0,
            createdCount: Number(d.createdCount) || 0,
            approvedCount: Number(d.approvedCount) || 0,
          })),
        );
      })
      // eslint-disable-next-line no-console
      .catch(e => console.error(e));
  }, []);

  // Bars show the COUNT of proposals per month (every proposal counts; amounts are
  // only sometimes recorded). The $ amount is shown in the tooltip.
  const barData = React.useMemo(
    () =>
      rows.map(d => ({
        label:
          MONTH_FULL_LIST[d.month - 1]?.slice(0, 3) ||
          String(d.month),
        'Proposals Created': d.createdCount,
        'Proposals Approved': d.approvedCount,
        createdAmount: d.proposalsCreated,
        approvedAmount: d.proposalsApproved,
      })),
    [rows],
  );

  const hasData =
    rows.length > 0 &&
    rows.some(d => d.createdCount > 0 || d.approvedCount > 0);

  const year = rows[0]?.year ?? new Date().getFullYear();

  return (
    <Container>
      <Header>
        Monthly Sales Report
        <InfoHint
          arrow
          placement="top"
          title={PROPOSAL_GRAPH_DESCRIPTION}
        >
          <InfoIcon fontSize="small" />
        </InfoHint>
      </Header>

      {hasData ? (
        <Bar>
          <ResponsiveBar
            data={barData}
            animate={false}
            enableLabel={false}
            keys={PROPOSAL_KEYS}
            indexBy="label"
            colors={[theme.marker1, theme.marker4]}
            padding={0.28}
            innerPadding={2}
            borderRadius={3}
            valueScale={{ type: 'linear' }}
            groupMode="grouped"
            margin={{
              top: 32,
              right: 16,
              bottom: 40,
              left: 48,
            }}
            enableGridY
            theme={chartTheme}
            axisLeft={{
              tickPadding: 8,
              tickSize: 0,
              format: (v: any) =>
                Number.isInteger(v) ? v : '',
            }}
            axisBottom={{ tickPadding: 6, tickSize: 0 }}
            tooltip={({ id, value, color, data: bar }: any) => {
              const isApproved = id === 'Proposals Approved';
              const amount = isApproved
                ? bar?.approvedAmount
                : bar?.createdAmount;
              const n = Number(value) || 0;
              return (
                <TooltipBox>
                  <TooltipMonth>
                    {bar?.label} {year} · {id}
                  </TooltipMonth>
                  <TooltipRow>
                    <Swatch
                      style={{ background: color as string }}
                    />
                    <strong>
                      {n} proposal{n === 1 ? '' : 's'}
                    </strong>
                    <Muted>
                      {' '}
                      ({currencyFormat.format(
                        Number(amount) || 0,
                      )}{' '}
                      recorded)
                    </Muted>
                  </TooltipRow>
                </TooltipBox>
              );
            }}
            legends={[
              {
                dataFrom: 'keys',
                anchor: 'top-right',
                direction: 'row',
                translateY: -24,
                translateX: 0,
                itemsSpacing: 16,
                itemWidth: 150,
                itemHeight: 18,
                symbolShape: 'square',
                symbolSize: 12,
                itemTextColor: '#334155',
              },
            ]}
          />
        </Bar>
      ) : (
        <EmptyState>No proposals yet this year</EmptyState>
      )}
    </Container>
  );
};

const Container = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-xs);
  padding: 24px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
`;

const Bar = styled.div`
  height: 400px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--mainText);
  margin-bottom: 20px;
`;

const InfoIcon = styled(InfoOutlinedIcon)`
  color: var(--neutral-400);
  cursor: help;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
  }
`;

const TooltipBox = styled.div`
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 8px 12px;
  box-shadow: 0 6px 20px rgba(19, 23, 32, 0.12);
  font-size: 0.8125rem;
  color: var(--neutral-700);
`;

const TooltipMonth = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 4px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  > strong {
    color: var(--neutral-900);
    font-weight: 700;
  }
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  flex-shrink: 0;
`;

const Muted = styled.span`
  color: var(--neutral-500);
`;

const EmptyState = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

export default MonthlySales;
